import { useEffect } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import { getUserName, useEffectOnce } from 'shared/lib/utils';
import { useAppSelector } from 'shared/lib/redux';
import { RealtimeCallback } from 'shared/lib/realtime';
import { loggerService } from 'shared/lib/logger';
import { eventService, getPage, XApiEventKeys } from 'shared/api/event-service';
import { getAmpConfigFlags } from 'shared/config/amp-config';
import {
  StreamPreview,
  MAIN_PAGE_ITEMS_LIMIT_PER_QUERY,
} from 'widgets/stream-preview';
import { Avatar } from 'widgets/avatar';
import { Programs } from 'widgets/programs';
import { QuickLinks } from 'widgets/quick-links';
import { RecommendationsPreview } from 'widgets/recommendations';
import {
  RecentlyVisitedLocations,
  getRecentlyVisitedLocations,
} from 'widgets/recently-visited-locations/';
import { useUserProgramsData } from 'widgets/programs/model';
import {
  useWalkthroughStatus,
  Walkthrough,
  WalkthroughAccessButton,
  useIsWalkthroughActivatedManually,
} from 'features/walkthrough';
import {
  AssignmentPanel,
  AssignmentActionNotificationPopup,
} from 'features/assignment-panel';
import {
  RESPONSIVE_BIG_MOBILE,
  RESPONSIVE_SMALL_MOBILE,
  RESPONSIVE_TABLET,
  useScreenWidth,
} from 'features/responsive';
import {
  AcceptedInviteAlert,
  useAcceptInvite,
  useIsAcceptedInviteAlertVisibleSelector,
} from 'features/accepted-invite-alert';
import { useAllClassesData } from 'entities/classes';
import { useAssignments, useRealtimeAssignments } from 'entities/assignments';

import styles from './styles.module.scss';

const HomePage = () => {
  const { t } = useTranslation();

  const {
    desktopCountPerSlide,
    tabletCountPerSlide,
    bigMobileCountPerSlide,
    smallMobileCountPerSlide,
  } = getRecentlyVisitedLocations(t);

  const {
    isAssignmentPanelOpened,
    isAssignmentActionNotificationActive,
    editingAssignment,
  } = useAppSelector((state) => state.assignmentPanel);
  const isAcceptedInviteAlertVisible =
    useIsAcceptedInviteAlertVisibleSelector();

  const isWalkthroughActivatedManually = useIsWalkthroughActivatedManually();
  const { allClassesKeys } = useAllClassesData();

  const { refetch: refetchStreamAssignments } = useAssignments({
    offset: 0,
    limit: MAIN_PAGE_ITEMS_LIMIT_PER_QUERY,
    selectedClass: allClassesKeys,
  });

  const { isLoading: isUserProgramsLoading } = useUserProgramsData();
  const isWalkthroughCompleted = useWalkthroughStatus();

  const isWalkthroughVisible =
    (!isWalkthroughCompleted && !isUserProgramsLoading) ||
    isWalkthroughActivatedManually;

  const teacherName = getUserName();

  const width = useScreenWidth();
  const isTabletLayout = width <= RESPONSIVE_TABLET;
  const isBigMobileLayout = width < RESPONSIVE_BIG_MOBILE;
  const isSmallMobileLayout = width < RESPONSIVE_SMALL_MOBILE;

  const { isRecentlyVisitedActivitiesEnabled } = getAmpConfigFlags();

  useAcceptInvite();

  /*
   * Replace home page url by removing '#/' from the end,
   * '#/' part is appended by react-router after navigating to home page from other pages.
   * Takes into account VITE_APP_ROUTER_BASENAME env variable to replace history state with correct url for PR preview.
   * */
  useEffect(() => {
    if (window.location.href.endsWith('#/')) {
      const locationPathname = import.meta.env.VITE_APP_ROUTER_BASENAME
        ? `${import.meta.env.VITE_APP_ROUTER_BASENAME}/home/educator`
        : '/home/educator';

      const pageUrl = `${window.location.origin}${locationPathname}`;

      window.history.replaceState(null, '', pageUrl);
    }
  }, []);

  useEffectOnce(
    () =>
      eventService.send(XApiEventKeys.viewed, undefined, {
        result: {
          isRightClick: false,
          url: window.location.href,
          page: getPage(),
        },
      }),
    [],
  );

  const refetchAllAssignments = () => {
    // perform requests for both stream and scheduled sections to cover case when assignment becomes scheduled/starts
    try {
      refetchStreamAssignments();
    } catch (e) {
      loggerService.logError(e);
    }
  };

  const handleRealtimeAssignments: RealtimeCallback = (eventType, payload) => {
    const { isFromEducatorHome } = payload;

    // do not re-fetch assignments if realtime update is coming from Educator Home
    // as it will be done via query invalidation after PUT or DELETE request
    if (isFromEducatorHome) return;

    refetchAllAssignments();
  };

  useRealtimeAssignments({
    assignmentAssignedCallback: refetchAllAssignments,
    assignmentUpdatedCallback: handleRealtimeAssignments,
    classesKeys: allClassesKeys,
  });

  const getRecentlyVisitedLocationsItemsPerSlide = () => {
    if (isSmallMobileLayout) return smallMobileCountPerSlide;

    if (isBigMobileLayout) return bigMobileCountPerSlide;

    if (isTabletLayout) return tabletCountPerSlide;

    return desktopCountPerSlide;
  };

  return (
    <>
      <div className="page layout-context-compact container">
        <div className="row">
          <div className={clsx('col-lg-3 col-sm-12', styles.sideNavigation)}>
            <div className={styles.avatarSection}>
              <Avatar />
              <h3 className={styles.welcomingMessage}>
                {`${t('Welcome')}, ${teacherName}!`}
              </h3>
            </div>
            {isTabletLayout && isAcceptedInviteAlertVisible && (
              <div className="rhythm-mb-2">
                <AcceptedInviteAlert />
              </div>
            )}
            {isRecentlyVisitedActivitiesEnabled && isTabletLayout && (
              <RecentlyVisitedLocations
                itemsPerSlide={getRecentlyVisitedLocationsItemsPerSlide()}
              />
            )}
            <Programs />
            <QuickLinks />
          </div>
          <div className={clsx('col-lg-9 col-sm-12', styles.mainContent)}>
            {!isTabletLayout && isAcceptedInviteAlertVisible && (
              <div className="rhythm-mb-2">
                <AcceptedInviteAlert />
              </div>
            )}
            {isRecentlyVisitedActivitiesEnabled && !isTabletLayout && (
              <RecentlyVisitedLocations
                itemsPerSlide={getRecentlyVisitedLocationsItemsPerSlide()}
              />
            )}
            <RecommendationsPreview />
            <StreamPreview />
          </div>
        </div>
      </div>
      {isAssignmentPanelOpened && editingAssignment && <AssignmentPanel />}
      {isAssignmentActionNotificationActive && (
        <AssignmentActionNotificationPopup />
      )}
      {isWalkthroughVisible && <Walkthrough />}
      <WalkthroughAccessButton />
    </>
  );
};

export default HomePage;
